"use client";

// ===========================================
// konakla - Global Error Boundary
// ===========================================
//
// `app/error.tsx` bir sayfadaki render hatasını yakalar ama RootLayout'u
// bozmaz. `app/global-error.tsx` ise RootLayout bile crash ettiğinde devreye
// girer (layout-boundary). Bu dosya kendi <html>/<body>'sini render etmek
// zorunda — layout'a güvenemeyiz.
//
// Sentry: Next.js 16+ için Sentry otomatik `captureReactException`'a bind
// edilebilir, ama `error.tsx`'teki manuel captureException kalıbını burada
// da tekrar ediyoruz — hem SSR hata'larda hem client root crash'lerde event
// Sentry'ye gider.

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    Sentry.captureException(error);
    console.error("[GlobalError]", error);
  }, [error]);

  return (
    <html lang="tr">
      <body
        style={{
          margin: 0,
          fontFamily: "system-ui, -apple-system, sans-serif",
          background: "#ffffff",
          color: "#111",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
      >
        <div style={{ maxWidth: 480, textAlign: "center" }}>
          <h1 style={{ fontSize: "1.75rem", marginBottom: "0.75rem" }}>
            Beklenmeyen bir hata oluştu
          </h1>
          <p style={{ color: "#555", marginBottom: "1.5rem", lineHeight: 1.5 }}>
            konakla geçici olarak yüklenemedi. Lütfen sayfayı yenilemeyi deneyin. Sorun devam ederse
            kısa bir süre sonra tekrar ziyaret edebilirsiniz.
          </p>
          {error.digest && (
            <p
              style={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                color: "#888",
                marginBottom: "1.5rem",
              }}
            >
              Hata kodu: {error.digest}
            </p>
          )}
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
            <button
              onClick={reset}
              style={{
                background: "#162ee0",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "0.75rem 1.25rem",
                fontSize: "0.95rem",
                cursor: "pointer",
              }}
            >
              Tekrar Dene
            </button>
            {/*
             * global-error root layout bile crash olduğunda render edilir;
             * Next router context'i güvenilir değil. `<a>` kullanmak burada
             * en güvenli seçenek (next/link yerine).
             */}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a
              href="/"
              style={{
                background: "white",
                color: "#162ee0",
                border: "2px solid #162ee0",
                borderRadius: "8px",
                padding: "0.75rem 1.25rem",
                fontSize: "0.95rem",
                textDecoration: "none",
              }}
            >
              Ana Sayfa
            </a>
          </div>
        </div>
      </body>
    </html>
  );
}
